import { useEffect, useState } from "react";
import { IdCard } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PatientNav from "@/components/PatientNav";

export default function PatientProfile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: "", phone: "", email: "", birthdate: "", gender: "", address: "",
    emergency_contact_name: "", emergency_contact_phone: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setForm({
      name: user.name || "", phone: user.phone || "", email: user.email || "",
      birthdate: user.birthdate || "", gender: user.gender || "", address: user.address || "",
      emergency_contact_name: user.emergency_contact_name || "",
      emergency_contact_phone: user.emergency_contact_phone || "",
    });
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/patients/me", form);
      setUser(data);
      toast.success("Perfil actualizado");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PatientNav />
      <div className="mx-auto max-w-xl px-6 py-8">
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Mi perfil</h1>
        <p className="mt-1 text-sm font-semibold text-slate-500">Actualiza tus datos personales.</p>

        <form onSubmit={save} className="mt-6 space-y-4 rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Nombre completo</Label>
            <Input required value={form.name} onChange={set("name")} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="pp-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Teléfono</Label>
              <Input value={form.phone} onChange={set("phone")} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="pp-phone" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Correo</Label>
              <Input required type="email" value={form.email} onChange={set("email")} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="pp-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Fecha de nacimiento</Label>
              <Input type="date" value={form.birthdate} onChange={set("birthdate")} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="pp-birthdate" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Género</Label>
              <Select value={form.gender} onValueChange={(v) => setForm((f) => ({ ...f, gender: v }))}>
                <SelectTrigger className="mt-1.5 rounded-xl border-[#E2E8F0] font-bold" data-testid="pp-gender"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Femenino">Femenino</SelectItem>
                  <SelectItem value="Masculino">Masculino</SelectItem>
                  <SelectItem value="Otro">Otro</SelectItem>
                  <SelectItem value="Prefiero no decir">Prefiero no decir</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Dirección</Label>
            <Input value={form.address} onChange={set("address")} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="pp-address" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Contacto de emergencia</Label>
              <Input value={form.emergency_contact_name} onChange={set("emergency_contact_name")} placeholder="Nombre" className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="pp-ec-name" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Tel. de emergencia</Label>
              <Input value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")} placeholder="Teléfono" className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="pp-ec-phone" />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="pp-save">
            <IdCard className="mr-1.5 h-4 w-4" /> {busy ? "Guardando…" : "Guardar cambios"}
          </Button>
        </form>
      </div>
    </div>
  );
}
